import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import Api from '../../service';
import UsuarioService from '../../service/usuario';
import Cabecalho from '../cabecalho/Cabecalho';
import Rodape from '../rodape/Rodape';
import Menu from '../menu/Menu';
import Titulo from '../titulo/Titulo';

const Icon = styled.svg`
    width: 16px;
    height: 16px;
    cursor: pointer;
    fill: #008080;
    transition: fill 0.2s;

    &:hover {
        fill: #005959;
    }
`;

const PencilIcon = () => (
    <Icon viewBox="0 0 24 24">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
    </Icon>
);

const CheckIcon = () => (
    <Icon viewBox="0 0 24 24">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
    </Icon>
);

const CloseIcon = () => (
    <Icon viewBox="0 0 24 24">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
    </Icon>
);

const CopyIcon = () => (
    <Icon viewBox="0 0 24 24">
        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
    </Icon>
);

const perfisValidos = ['admin', 'coordenador-geral', 'coordenador-ponto', 'coordenador-regional', 'publicador', 'colaborador', 'secretaria'];

const usuarioLogadoEAdmin = () => {
    try {
        return (JSON.parse(localStorage.getItem('perfilUsuario') || '[]') || []).includes('admin');
    } catch {
        return false;
    }
};
const privilegiosValidos = ['anciao', 'servo-ministerial', 'pioneiro-regular'];

export default function GerenciarPublicadores() {
    const [query, setQuery] = useState('');
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editedRows, setEditedRows] = useState({});
    const [editingCell, setEditingCell] = useState(null); // { userId, field }
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [filtroPerfil, setFiltroPerfil] = useState('todos');
    const [filtroCoordenador, setFiltroCoordenador] = useState('todos');

    const debounceRef = useRef(null);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    useEffect(() => {
        fetchUsuarios('', 1, 10, 'todos', 'todos');
    }, []);

    function fetchUsuarios(termoBusca, pageNumber = 1, pageSize = limit, perfil = filtroPerfil, coord = filtroCoordenador) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            setError(null);
            
            try {
                const params = {
                    search: termoBusca.trim(),
                    page: pageNumber,
                    limit: pageSize,
                    filtroPerfil: perfil,
                    filtroCoordenador: coord
                };
                const response = await Api.get('usuario/buscar-simples', { params });
                
                if (response.data.usuarios) {
                    setUsuarios(response.data.usuarios);
                    setTotalPages(response.data.totalPages);
                    setPage(response.data.page);
                } else {
                    setUsuarios(response.data);
                }
            } catch (err) {
                console.error('Erro ao buscar usuários:', err);
                setError(err.response?.data?.erro || 'Erro ao buscar usuários');
                showToast('Erro ao buscar usuários', 'error');
            } finally {
                setLoading(false);
            }
        }, 300);
    }

    function onChangeSearch(e) {
        const valor = e.target.value;
        setQuery(valor);
        setPage(1);
        fetchUsuarios(valor, 1, limit, filtroPerfil, filtroCoordenador);
    }

    function handleLimitChange(e) {
        const newLimit = parseInt(e.target.value);
        setLimit(newLimit);
        setPage(1);
        fetchUsuarios(query, 1, newLimit, filtroPerfil, filtroCoordenador);
    }

    function handleFiltroPerfilChange(e) {
        const novoPerfil = e.target.value;
        setFiltroPerfil(novoPerfil);
        setPage(1);
        // Resetar filtro de coordenador se sair de coordenadores
        const novoCoord = novoPerfil === 'coordenadores' ? filtroCoordenador : 'todos';
        setFiltroCoordenador(novoCoord);
        fetchUsuarios(query, 1, limit, novoPerfil, novoCoord);
    }

    function handleFiltroCoordenadorChange(e) {
        const novoCoord = e.target.value;
        setFiltroCoordenador(novoCoord);
        setPage(1);
        fetchUsuarios(query, 1, limit, filtroPerfil, novoCoord);
    }

    function handlePageChange(newPage) {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            fetchUsuarios(query, newPage, limit, filtroPerfil, filtroCoordenador);
        }
    }

    const handleStartEdit = (userId, field) => {
        setEditingCell({ userId, field });
        const user = usuarios.find(u => u._id === userId);
        // Pre-fill editedRows with current value
        setEditedRows(prev => ({
            ...prev,
            [userId]: {
                ...prev[userId],
                [field]: user[field]
            }
        }));
    };

    const handleCancelEdit = () => {
        const { userId, field } = editingCell;
        setEditingCell(null);
        // Clear only the specific field being edited from the temporary state
        setEditedRows(prev => {
            const userChanges = { ...prev[userId] };
            delete userChanges[field];
            if (Object.keys(userChanges).length === 0) {
                const newState = { ...prev };
                delete newState[userId];
                return newState;
            }
            return { ...prev, [userId]: userChanges };
        });
    };

    const handleConfirmCellEdit = async () => {
        if (!editingCell) return;
        const { userId, field } = editingCell;
        const userChanges = editedRows[userId];

        if (!userChanges || userChanges[field] === undefined) {
            handleCancelEdit();
            return;
        }

        const originalUser = usuarios.find(u => u._id === userId);
        if (originalUser[field] === userChanges[field]) {
            handleCancelEdit();
            return;
        }

        try {
            await UsuarioService.updateUsuario(userId, { [field]: userChanges[field] });
            setUsuarios(prev => prev.map(u => u._id === userId ? { ...u, [field]: userChanges[field] } : u));
            showToast("Campo atualizado com sucesso!", "success");
        } catch (error) {
            console.error(`Erro ao salvar o campo ${field}:`, error);
            showToast(`Erro ao salvar o campo ${field}.`, "error");
        } finally {
            handleCancelEdit();
        }
    };

    const handleInputChange = (userId, field, value) => {
        setEditedRows(prev => ({
            ...prev,
            [userId]: {
                ...prev[userId],
                [field]: value
            }
        }));
    };

    const toggleArrayItem = (id, field, item) => {
        setEditedRows(prev => {
            const currentRow = prev[id] || {};
            const currentList = currentRow[field] !== undefined 
                ? currentRow[field] 
                : (usuarios.find(u => u._id === id)[field] || []);
            
            let newList;
            if (currentList.includes(item)) {
                newList = currentList.filter(i => i !== item);
            } else {
                newList = [...currentList, item];
            }
            
            return {
                ...prev,
                [id]: {
                    ...currentRow,
                    [field]: newList
                }
            };
        });
    };

    const arraysEqualUnordered = (a = [], b = []) => {
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        const sa = [...a].sort();
        const sb = [...b].sort();
        for (let i = 0; i < sa.length; i++) {
            if (sa[i] !== sb[i]) return false;
        }
        return true;
    };

    const handleSave = async (id) => {
        const changes = editedRows[id];
        if (!changes) return;

        try {
            await UsuarioService.updateUsuario(id, changes);
            setUsuarios(prev => prev.map(u => u._id === id ? { ...u, ...changes } : u));
            setEditedRows(prev => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
            });
            showToast("Usuário atualizado com sucesso!", "success");
        } catch (error) {
            console.error("Erro ao salvar usuário:", error);
            // Se a API retornar uma mensagem de erro, exibe no toast
            const apiMsg = error?.response?.data?.erro || error?.message || "Erro ao salvar usuário.";
            showToast(apiMsg, "error");
        }
    };

    const handleCancel = (id) => {
        setEditedRows(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });
    };

    const handleCopy = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            showToast("Copiado!", "success");
        }).catch(() => {
            showToast("Erro ao copiar.", "error");
        });
    };

    const getDisplayValue = (usuario, field) => {
        if (editedRows[usuario._id] && editedRows[usuario._id][field] !== undefined) {
            return editedRows[usuario._id][field];
        }
        return usuario[field];
    };

    return (
        <PageWrapper>
            <ToastContainer>
                <ToastMessage show={toast.show} type={toast.type}>
                    {toast.message}
                </ToastMessage>
            </ToastContainer>
            <Menu />
            <Cabecalho />
            <MainContent>
                <Container>
                    <Titulo>Gerenciar Publicadores</Titulo>
                    <CampoBusca>
                        <InputBusca
                            type="text"
                            placeholder="Buscar por nome, email ou telefone..."
                            value={query}
                            onChange={onChangeSearch}
                        />
                    </CampoBusca>
                    <FiltrosContainer>
                        <FiltroGroup>
                            <Label>Filtrar por Perfil:</Label>
                            <SelectFilter value={filtroPerfil} onChange={handleFiltroPerfilChange}>
                                <option value="todos">Todos os Perfis</option>
                                <option value="coordenadores">Coordenadores</option>
                                <option value="publicadores">Publicadores</option>
                                {usuarioLogadoEAdmin() && <option value="administradores">Administradores</option>}
                            </SelectFilter>
                        </FiltroGroup>
                        <FiltroGroup>
                            <Label>Tipo de Coordenação:</Label>
                            <SelectFilter 
                                value={filtroCoordenador} 
                                onChange={handleFiltroCoordenadorChange}
                                disabled={filtroPerfil !== 'coordenadores'}
                            >
                                <option value="todos">Todos os Tipos</option>
                                <option value="geral">Geral</option>
                                <option value="regional">Regionais</option>
                                <option value="ponto">Ponto</option>
                            </SelectFilter>
                        </FiltroGroup>
                    </FiltrosContainer>
                    {loading && <MensagemLoading>Carregando...</MensagemLoading>}
                    {error && <MensagemErro>{error}</MensagemErro>}
                    <TabelaContainer>
                        <TabelaUsuarios>
                            <thead>
                        {/* Corrigido: uso do componente Titulo importado */}
                                <LinhaCabecalho>
                                    <CelulaCabecalho style={{ width: '20%' }}>Nome</CelulaCabecalho>
                                    <CelulaCabecalho style={{ width: '22%' }}>Email</CelulaCabecalho>
                                    <CelulaCabecalho style={{ width: '13%' }}>Telefone</CelulaCabecalho>
                                    <CelulaCabecalho style={{ width: '13%' }}>Usuário</CelulaCabecalho>
                                    <CelulaCabecalho style={{ width: '22%' }}>Perfil / Privilégio</CelulaCabecalho>
                                    <CelulaCabecalho style={{ width: '10%' }}>Ação</CelulaCabecalho>
                                </LinhaCabecalho>
                            </thead>
                            <tbody>
                                {!loading && usuarios.length === 0 && (
                                    <LinhaVazia>
                                        <CelulaVazia colSpan="6">Nenhum usuário encontrado</CelulaVazia>
                                    </LinhaVazia>
                                )}
                                {usuarios.map(usuario => {
                                    const isEdited = !!editedRows[usuario._id];
                                    const isEditing = (field) => editingCell && editingCell.userId === usuario._id && editingCell.field === field;

                                    return (
                                    <LinhaUsuario key={usuario._id}>
                                        <CelulaUsuario>
                                            <CellContent>
                                                {isEditing('nome') ? (
                                                    <EditInput 
                                                        value={getDisplayValue(usuario, 'nome')}
                                                        onChange={(e) => handleInputChange(usuario._id, 'nome', e.target.value)}
                                                    />
                                                ) : (
                                                    <span>{usuario.nome}</span>
                                                )}
                                                <IconContainer>
                                                    {isEditing('nome') ? (
                                                        <>
                                                            <div onClick={handleConfirmCellEdit}><CheckIcon /></div>
                                                            <div onClick={handleCancelEdit}><CloseIcon /></div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div onClick={() => handleCopy(usuario.nome)} title="Copiar"><CopyIcon /></div>
                                                            <div onClick={() => handleStartEdit(usuario._id, 'nome')} title="Editar"><PencilIcon /></div>
                                                        </>
                                                    )}
                                                </IconContainer>
                                            </CellContent>
                                        </CelulaUsuario>
                                        <CelulaUsuario>
                                            <CellContent>
                                                {isEditing('email') ? (
                                                    <EditInput 
                                                        value={getDisplayValue(usuario, 'email')}
                                                        onChange={(e) => handleInputChange(usuario._id, 'email', e.target.value)}
                                                    />
                                                ) : (
                                                    <span>{usuario.email}</span>
                                                )}
                                                <IconContainer>
                                                    {isEditing('email') ? (
                                                        <>
                                                            <div onClick={handleConfirmCellEdit}><CheckIcon /></div>
                                                            <div onClick={handleCancelEdit}><CloseIcon /></div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div onClick={() => handleCopy(usuario.email)} title="Copiar"><CopyIcon /></div>
                                                            <div onClick={() => handleStartEdit(usuario._id, 'email')} title="Editar"><PencilIcon /></div>
                                                        </>
                                                    )}
                                                </IconContainer>
                                            </CellContent>
                                        </CelulaUsuario>
                                        <CelulaUsuario>
                                            <CellContent>
                                                {isEditing('telefone') ? (
                                                    <EditInput 
                                                        value={getDisplayValue(usuario, 'telefone')}
                                                        onChange={(e) => handleInputChange(usuario._id, 'telefone', e.target.value)}
                                                    />
                                                ) : (
                                                    <span>{usuario.telefone}</span>
                                                )}
                                                <IconContainer>
                                                    {isEditing('telefone') ? (
                                                        <>
                                                            <div onClick={handleConfirmCellEdit}><CheckIcon /></div>
                                                            <div onClick={handleCancelEdit}><CloseIcon /></div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div onClick={() => handleCopy(usuario.telefone)} title="Copiar"><CopyIcon /></div>
                                                            <div onClick={() => handleStartEdit(usuario._id, 'telefone')} title="Editar"><PencilIcon /></div>
                                                        </>
                                                    )}
                                                </IconContainer>
                                            </CellContent>
                                        </CelulaUsuario>
                                        <CelulaUsuario>
                                            <CellContent>
                                                {isEditing('usuario') ? (
                                                    <EditInput 
                                                        value={getDisplayValue(usuario, 'usuario')}
                                                        onChange={(e) => handleInputChange(usuario._id, 'usuario', e.target.value)}
                                                    />
                                                ) : (
                                                    <span>{usuario.usuario || ''}</span>
                                                )}
                                                <IconContainer>
                                                    {isEditing('usuario') ? (
                                                        <>
                                                            <div onClick={handleConfirmCellEdit}><CheckIcon /></div>
                                                            <div onClick={handleCancelEdit}><CloseIcon /></div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div onClick={() => handleCopy(usuario.usuario)} title="Copiar"><CopyIcon /></div>
                                                            <div onClick={() => handleStartEdit(usuario._id, 'usuario')} title="Editar"><PencilIcon /></div>
                                                        </>
                                                    )}
                                                </IconContainer>
                                            </CellContent>
                                        </CelulaUsuario>
                                        <CelulaUsuario>
                                            <MultiSelectContainer style={{ flexDirection: 'column', gap: '6px' }}>
                                                <div>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#008080', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Perfil</span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                        {perfisValidos.filter(p => p !== 'admin' || usuarioLogadoEAdmin()).map(perfil => (
                                                            <label key={perfil} style={{ display: 'flex', alignItems: 'center', fontSize: '11px', marginRight: '4px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={(getDisplayValue(usuario, 'perfil') || []).includes(perfil)}
                                                                    onChange={() => toggleArrayItem(usuario._id, 'perfil', perfil)}
                                                                />
                                                                &nbsp;{perfil}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div style={{ borderTop: '1px solid #eee', paddingTop: '4px' }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#5a7a5a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Privilégio</span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                        {privilegiosValidos.map(priv => (
                                                            <label key={priv} style={{ display: 'flex', alignItems: 'center', fontSize: '11px', marginRight: '4px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={(getDisplayValue(usuario, 'privilegio') || []).includes(priv)}
                                                                    onChange={() => toggleArrayItem(usuario._id, 'privilegio', priv)}
                                                                />
                                                                &nbsp;{priv}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </MultiSelectContainer>
                                        </CelulaUsuario>
                                        <CelulaUsuario>
                                            {(() => {
                                                const edited = editedRows[usuario._id] || {};
                                                const perfilEdited = edited.perfil !== undefined &&
                                                    !arraysEqualUnordered(edited.perfil || [], usuario.perfil || []);
                                                const privilegioEdited = edited.privilegio !== undefined &&
                                                    !arraysEqualUnordered(edited.privilegio || [], usuario.privilegio || []);
                                                const hasChanges = perfilEdited || privilegioEdited;
                                                const saveDisabled = !hasChanges || (toast.show && toast.type === 'error');
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                        <ActionButton 
                                                            disabled={saveDisabled}
                                                            onClick={() => handleSave(usuario._id)}
                                                        >
                                                            Salvar
                                                        </ActionButton>
                                                        {hasChanges && (
                                                            <CancelButton onClick={() => handleCancel(usuario._id)}>
                                                                Cancelar
                                                            </CancelButton>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </CelulaUsuario>
                                    </LinhaUsuario>
                                )})}
                            </tbody>
                        </TabelaUsuarios>
                    </TabelaContainer>
                    
                    {!loading && usuarios.length > 0 && (
                        <PaginationContainer>
                            <PageButton 
                                onClick={() => handlePageChange(page - 1)} 
                                disabled={page === 1}
                            >
                                Anterior
                            </PageButton>
                            <span style={{ fontSize: '14px', color: '#666' }}>
                                Página {page} de {totalPages}
                            </span>
                            <PageButton 
                                onClick={() => handlePageChange(page + 1)} 
                                disabled={page === totalPages}
                            >
                                Próxima
                            </PageButton>

                            <SelectLimit value={limit} onChange={handleLimitChange}>
                                <option value={10}>10 por página</option>
                                <option value={15}>15 por página</option>
                                <option value={25}>25 por página</option>
                                <option value={999999}>Ver tudo</option>
                            </SelectLimit>
                        </PaginationContainer>
                    )}
                </Container>
            </MainContent>
            <Rodape />
        </PageWrapper>
    );
}

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    gap: 15px;
`;

const PageButton = styled.button`
    padding: 8px 16px;
    border: 1px solid #008080;
    background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
    color: ${props => props.disabled ? '#ccc' : '#008080'};
    border-radius: 4px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-size: 14px;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background-color: #008080;
        color: white;
    }
`;

const PageWrapper = styled.div`
    min-height: 100vh;
    background: #fff;
`;

const MainContent = styled.main`
    padding-top: 85px;
    padding-left: 260px;
    padding-right: 24px;
    padding-bottom: 60px;
    box-sizing: border-box;
    @media (max-width: 1200px) and (min-width: 768px) {
        padding-left: 80px;
    }
    @media (max-width: 767px) {
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 16px;
    }
`;

const Container = styled.div`
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
    font-family: 'Roboto', sans-serif;
`;

// Removido: styled Titulo duplicado

const CampoBusca = styled.div`
    margin-bottom: 20px;
`;

const FiltrosContainer = styled.div`
    display: flex;
    gap: 24px;
    margin-bottom: 32px;
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 16px;
    }
`;

const FiltroGroup = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 200px;
`;

const Label = styled.label`
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    font-size: 16px;
`;

const SelectFilter = styled.select`
    padding: 8px 16px;
    border: 1px solid #008080;
    background-color: white;
    color: #008080;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    outline: none;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        background: #008080;
        color: #fff;
    }

    &:focus {
        box-shadow: none;
    }
    
    option {
        background-color: #fff !important;
        color: #333 !important;
    }

    &:disabled {
        border-color: #ddd;
        background-color: #f5f5f5;
        color: #aaa;
        cursor: not-allowed;
    }
`;

const SelectLimit = styled.select`
    padding: 8px 16px;
    border: 1px solid #008080;
    background-color: white;
    color: #008080;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    outline: none;
    transition: all 0.2s;

    &:hover {
        background-color: #008080;
        color: white;
    }

    option {
        background-color: white;
        color: #333;
    }
`;

const InputBusca = styled.input`
    width: 100%;
    max-width: 500px;
    padding: 12px 16px;
    font-size: 16px;
    border: 1px solid #ddd;
    border-radius: 6px;
    transition: border-color 0.2s;
    &:focus {
        outline: none;
        border-color: #008080;
        box-shadow: 0 0 0 3px rgba(0, 128, 128, 0.1);
    }
`;

const EditInput = styled.input`
    width: calc(100% - 40px);
    padding: 6px 8px;
    font-size: 15px;
    border: 1px solid #008080;
    border-radius: 4px;
    outline: none;
`;

const MensagemLoading = styled.div`
    padding: 12px;
    color: #666;
    font-style: italic;
`;

const MensagemErro = styled.div`
    padding: 12px 16px;
    background-color: #fee;
    color: #c33;
    border-radius: 6px;
    margin-bottom: 16px;
`;

const TabelaContainer = styled.div`
    overflow-x: auto;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const TabelaUsuarios = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 15px;
`;

const LinhaCabecalho = styled.tr`
    background-color: #008080;
`;

const CelulaCabecalho = styled.th`
    padding: 14px 16px;
    text-align: left;
    font-weight: 600;
    color: #fff;
    border-bottom: 2px solid #008080;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const LinhaUsuario = styled.tr`
    border-bottom: 1px solid #eee;
    transition: background-color 0.2s;
    &:hover {
        background-color: #f8f9fa;
    }
`;

const CelulaUsuario = styled.td`
    padding: 14px 16px;
    color: #333;
    font-size: 15px;
    position: relative;
`;

const CellContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const IconContainer = styled.div`
    display: flex;
    gap: 8px;
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    background: #f8f9fa;
    padding: 4px;
    border-radius: 4px;
    opacity: 0;
    transition: opacity 0.2s;

    ${LinhaUsuario}:hover & {
        opacity: 1;
    }
`;

const LinhaVazia = styled.tr``;
const CelulaVazia = styled.td`
    text-align: center;
    padding: 40px 16px !important;
    color: #999;
    font-style: italic;
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 100px;
  right: 20px;
  z-index: 9999;
`;

const ToastMessage = styled.div`
  background-color: ${props => props.type === 'error' ? '#f44336' : '#4caf50'};
  color: white;
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  margin-bottom: 10px;
  min-width: 250px;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
`;

const MultiSelectContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ActionButton = styled.button`
  background-color: ${props => props.disabled ? '#ccc' : '#008080'};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 12px;
  width: 100%;
`;

const CancelButton = styled.button`
  background-color: #d9534f;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  width: 100%;
  &:hover {
    background-color: #c9302c;
  }
`;